/**
 * Bounded preflight read: column headers + the first N sample rows from a `File`,
 * WITHOUT reading the whole file (for CSV).
 *
 * The output feeds two preflight needs (PRODUCT.md "PII Preflight"): the headers
 * go to the AI-suggestion endpoint, and the sample rows drive the LOCAL
 * email/phone/SSN value scan (`classifyColumnPii`) that runs entirely in the
 * browser — no cell values leave the page.
 *
 *   - **CSV:** slice the first `PREFLIGHT_SLICE_BYTES` (a memory safety cap so a
 *     pathological one-line file can't blow up) and Papa-parse that slice with
 *     `preview` capped to header + N rows. We never read past the slice.
 *   - **XLSX:** SheetJS must materialize the whole workbook (zip central directory
 *     + shared strings) to read any sheet, so it can't slice. We read it once with
 *     `sheetRows` limiting parsing to the first N rows. Files over
 *     `XLSX_SIZE_GUARD_BYTES` throw {@link XlsxTooLargeError} before parsing.
 *
 * `xlsx` is imported dynamically so it lands in a lazy uploader chunk rather than
 * the main bundle.
 *
 * Browser-only: uses the `File` API and (for CSV) Papa Parse's `FileReader` path.
 */

import Papa from 'papaparse';
import {
  PREFLIGHT_SAMPLE_ROWS,
  PREFLIGHT_SLICE_BYTES,
  XLSX_SIZE_GUARD_BYTES,
} from '../../common/uploader';
import type { ColumnHeader, UploadFormat } from '../../common/uploader';
import { XlsxTooLargeError } from './errors';
import { coerceXlsxCellValue } from './xlsx-cell';

/** The bounded preflight read result. */
export interface PreflightSample {
  /** Which parser the full scrub pass will use for this file. */
  readonly format: UploadFormat;
  /** Header row, by 0-based index. `headers[i].index === i`. */
  readonly headers: readonly ColumnHeader[];
  /**
   * Up to `PREFLIGHT_SAMPLE_ROWS` data rows, each aligned to `headers` by index.
   * Short rows are padded to the header width with empty strings so column-wise
   * sampling (the PII scan) stays index-aligned.
   */
  readonly sampleRows: readonly (readonly string[])[];
}

/**
 * Detect the upload format from the file. Extension-first (`.xlsx`), since the
 * browser-reported MIME type for spreadsheets is unreliable; everything else is
 * treated as CSV (the canonical text format).
 */
export function detectFormat(file: File): UploadFormat {
  return file.name.toLowerCase().endsWith('.xlsx') ? 'xlsx' : 'csv';
}

/** Trim a header cell and wrap it as a {@link ColumnHeader} at its position. */
function toHeaders(rawHeader: readonly string[]): ColumnHeader[] {
  return rawHeader.map((name, index) => ({ index, name: name.trim() }));
}

/** Pad/truncate a row to `width` so every sampled row is header-aligned. */
function alignRow(cells: readonly string[], width: number): string[] {
  const out = new Array<string>(width);
  for (let i = 0; i < width; i += 1) out[i] = cells[i] ?? '';
  return out;
}

/**
 * Read headers + the first N sample rows from a CSV/XLSX `File`. CSV is bounded
 * to a byte slice; XLSX reads the whole (size-guarded) workbook but parses only
 * the first N rows.
 *
 * @throws {XlsxTooLargeError} when an `.xlsx` exceeds `XLSX_SIZE_GUARD_BYTES`.
 */
export async function readPreflightSample(file: File): Promise<PreflightSample> {
  const format = detectFormat(file);
  return format === 'xlsx' ? readXlsxSample(file) : readCsvSample(file);
}

function readCsvSample(file: File): Promise<PreflightSample> {
  // Bound the read to a byte slice; `preview` stops parsing after the header +
  // sample rows even if the slice holds more.
  const slice = file.slice(0, PREFLIGHT_SLICE_BYTES);
  return new Promise<PreflightSample>((resolve, reject) => {
    Papa.parse<string[]>(slice as File, {
      preview: PREFLIGHT_SAMPLE_ROWS + 1, // +1 for the header row
      skipEmptyLines: 'greedy',
      complete: (results) => {
        const rows = results.data;
        if (rows.length === 0) {
          resolve({ format: 'csv', headers: [], sampleRows: [] });
          return;
        }
        const headers = toHeaders(rows[0] ?? []);
        const width = headers.length;
        const sampleRows = rows.slice(1).map((row) => alignRow(row, width));
        resolve({ format: 'csv', headers, sampleRows });
      },
      error: (error: Error) => {
        reject(error);
      },
    });
  });
}

async function readXlsxSample(file: File): Promise<PreflightSample> {
  if (file.size > XLSX_SIZE_GUARD_BYTES) {
    throw new XlsxTooLargeError(file.size, XLSX_SIZE_GUARD_BYTES);
  }
  // Dynamic import: keep SheetJS out of the main bundle (lazy uploader chunk).
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  // `sheetRows` caps parsing to the first N rows even though the bytes are fully
  // loaded. F4/F37: `cellDates` + `raw: true` read each cell's UNDERLYING STORED
  // VALUE (never the formatted display text), coerced through the ONE
  // `coerceXlsxCellValue` — identical to the full scrub pass in parse-stream.ts,
  // so the mapping-screen preview is byte-identical to what ingest stores.
  const workbook = XLSX.read(buffer, {
    type: 'array',
    sheetRows: PREFLIGHT_SAMPLE_ROWS + 1,
    cellDates: true,
  });
  const firstSheetName = workbook.SheetNames[0];
  if (firstSheetName === undefined) {
    return { format: 'xlsx', headers: [], sampleRows: [] };
  }
  const sheet = workbook.Sheets[firstSheetName];
  if (sheet === undefined) {
    return { format: 'xlsx', headers: [], sampleRows: [] };
  }
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: true,
    defval: '',
    blankrows: false,
  });
  if (matrix.length === 0) {
    return { format: 'xlsx', headers: [], sampleRows: [] };
  }
  const headers = toHeaders((matrix[0] ?? []).map((cell) => coerceXlsxCellValue(cell)));
  const width = headers.length;
  const sampleRows = matrix
    .slice(1)
    .map((row) => alignRow(row.map((cell) => coerceXlsxCellValue(cell)), width));
  return { format: 'xlsx', headers, sampleRows };
}
